import * as React from 'react';
import { useRouter } from 'next/router';
import { useAtom, useAtomValue } from 'jotai';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { imageVariant } from '@restworld/utils-common';
import { Text, Button, Popover, PopoverTrigger } from '@restworld/ui-ds-base';

import {
  getApplyJobStatusDataAtom,
  getIsSpontaneousApplicationAtom,
  isRestaurantPublicAtom,
} from '../application-details.context';
import { useJobPositionDetails } from '../../../../hooks';
import JobDetailBlock from '../../../../components/job-detail-block';

interface IApplicationInformationCardProps {
  bgColor?: 'primary' | 'none';
}

export const ApplicationInformationCard: React.FunctionComponent<IApplicationInformationCardProps> = ({ bgColor }) => {
  const [applyJobStatusData] = useAtom(getApplyJobStatusDataAtom);
  const jobPositionDetails = useJobPositionDetails({ jobPositionApiData: applyJobStatusData.job_position });
  const router = useRouter();
  const [isSpontaneousApplication] = useAtom(getIsSpontaneousApplicationAtom);
  const isPublicRestaurant = useAtomValue(isRestaurantPublicAtom);

  const { t } = useTranslation();

  return (
    <JobDetailBlock
      jobPositionDetails={
        applyJobStatusData.restaurant_id
          ? {
              companyName: applyJobStatusData.restaurant.employer?.business_name,
              // `companyLogo` is typed `string | undefined`, but imageVariant returns
              // `string | null | undefined` mirroring its input — narrow `null` → `undefined`.
              companyLogo: imageVariant(applyJobStatusData.restaurant.restaurant_logo, 'sm') ?? undefined,
              title: applyJobStatusData.restaurant.restaurant_name,
              jobLocation: applyJobStatusData.restaurant.address,
              jobLocality: applyJobStatusData.restaurant.locality,
              restaurantCovers: applyJobStatusData.restaurant?.covers_per_service_var?.label,
              restaurantStaffSize:
                (applyJobStatusData.restaurant?.staff_size_floor || 0) +
                (applyJobStatusData.restaurant?.staff_size_kitchen || 0),
            }
          : jobPositionDetails.jobPositionDetails
      }
      showRestaurant={
        applyJobStatusData.restaurant_id
          ? !!applyJobStatusData.restaurant.show_details
          : jobPositionDetails.showRestaurant
      }
      bgColor={bgColor}
      showShareButton={false}
    >
      {isSpontaneousApplication && !isPublicRestaurant ? (
        <Popover
          trigger={PopoverTrigger.CLICK}
          fullWidth={true}
          buttonClassName="min-w-full justify-center items-center gap-1.5 inline-flex text-primary-900 !bg-secondary border-secondary hover:bg-secondary hover:!text-primary-900 focus:outline-none focus:ring-0 focus:ring-offset-0 font-medium px-4 py-3"
          panelClassName="w-full"
          haveDefaultButtonStyle={true}
        >
          <Popover.Button>
            <span>{t('job-application-page:view_the_venue')}</span>
            <ArrowTopRightOnSquareIcon className="h-5 w-5 text-primary-900" />
          </Popover.Button>
          <Popover.Panel>
            <Text color="muted">{t('job-application-page:no_longer_publicly_available')} &#x1F625;</Text>
          </Popover.Panel>
        </Popover>
      ) : (
        <Button
          fullwidth
          className={'justify-center items-center gap-1.5 inline-flex'}
          onClick={() => {
            if (isSpontaneousApplication) router.push(`/locale/${applyJobStatusData.restaurant.slug}`);
            else router.push(`/posizione/${applyJobStatusData.job_position.semantic_unique_name}`);
          }}
        >
          {isSpontaneousApplication
            ? t('job-application-page:view_the_venue')
            : t('job-application-page:view_location')}
        </Button>
      )}
    </JobDetailBlock>
  );
};

export default ApplicationInformationCard;
